import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Play, 
  BarChart3, 
  ArrowRight,
  Youtube,
  Instagram,
  Sparkles,
  Globe,
  Mail,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/lib/LanguageContext';
import Logo from '@/components/Logo';

const featureIcons = [Youtube, Sparkles, BarChart3, Globe];
const featureKeys = ['discovery', 'subtitles', 'titles', 'publishing'];

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const { t, language, setLanguage, availableLanguages } = useLanguage();

  // Build features array from translation keys
  const features = featureKeys.map((key, index) => ({
    icon: featureIcons[index],
    title: t(`landing.features.${key}.title`),
    description: t(`landing.features.${key}.description`)
  }));

  useEffect(() => {
    if (referralCode) {
      localStorage.setItem('referral_code', referralCode);
    }
  }, [referralCode]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header - Fixed on mobile */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur-xl fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Logo className="w-8 h-8 sm:w-10 sm:h-10" textClassName="text-lg sm:text-xl" />
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Selector - Compact on mobile */}
            <div className="flex items-center">
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-1.5 sm:px-2 py-1 text-sm rounded transition-colors ${
                    language === lang.code 
                      ? 'bg-primary/20 text-primary' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={lang.name}
                >
                  {lang.flag}
                </button>
              ))}
            </div>
            <Link to="/login" className="hidden sm:block">
              <Button variant="ghost" size="sm" data-testid="login-btn">{t('landing.login')}</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="glow-primary text-xs sm:text-sm" data-testid="get-started-btn">
                {t('landing.getStarted')}
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main content - Scrollable, padded for fixed header */}
      <main className="pt-14 sm:pt-16">
        {/* Hero Section */}
        <section className="py-12 sm:py-20 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <Badge className="mb-4 sm:mb-6 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 text-primary border-primary/30 text-xs sm:text-sm">
              {t('landing.poweredByAI')}
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 sm:mb-6 leading-tight">
              {t('landing.heroTitle')}
              <span className="text-gradient"> {t('landing.heroHighlight')}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
              {t('landing.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link to="/register" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg glow-primary" data-testid="hero-cta-btn">
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  {t('landing.getStartedFree')}
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="w-full sm:w-auto px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg">
                {t('landing.watchDemo')}
              </Button>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-3 sm:mt-4">
              {t('landing.trustedBy')}
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 sm:py-20 px-4 sm:px-6 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-8 sm:mb-12">
              Everything You Need to Go Viral
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={`feature-${feature.title}`} className="bg-card border-border">
                    <CardContent className="p-4 sm:p-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-3 sm:mb-4">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                      <h3 className="font-heading font-semibold text-sm sm:text-lg mb-1 sm:mb-2">{feature.title}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground line-clamp-3">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-border">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-6 sm:gap-8">
              {/* Top Row */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <Logo className="w-8 h-8" textClassName="font-bold" />
                <div className="flex items-center gap-6">
                  <Youtube className="w-5 h-5 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
                  <Instagram className="w-5 h-5 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
                </div>
              </div>
              
              {/* Links Row */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm">
                <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                  Termos de Uso
                </Link>
                <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                  Política de Privacidade
                </Link>
              </div>
              
              {/* Contact Row */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm">
                <a 
                  href="mailto:legal@viralclipes" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  legal@viralclipes
                </a>
                <a 
                  href="mailto:privacy@viralclipes" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  privacy@viralclipes
                </a>
              </div>
              
              {/* Copyright */}
              <div className="text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  © {new Date().getFullYear()} Viral Clipes. Todos os direitos reservados.
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground/60 mt-2 px-4">
                  YouTube é marca registrada do Google LLC. TikTok é marca registrada da ByteDance Ltd. 
                  Instagram é marca registrada da Meta Platforms, Inc.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
